<script lang="ts" setup>
import Close from '@assets/icons/close.svg';
import Star from '@assets/icons/star.svg';
import { ref } from 'vue';

type Image = {
  url: String,
  width: String,
  height: String,
  title: String
}
type Props = {
  image: Image,
  heading: String,
  rating: number,
  description: String,
  achievement: String,
  simpleText: String,
  static: boolean
}

defineProps<Props>();

const toggle = ref(false);

</script>

<template>
  <div
    class="rounded-[24px] relative flex md:block w-full"
    :class="{'hover-shrink cursor-pointer': !toggle }"
    @click="toggle = true">

    <div class="flex flex-col bg-white h-full p-[24px] md:p-[32px] rounded-[24px] z-1 relative w-full"
      :class="{
        'text-white !bg-dark-teal toggled': toggle,
        '': !toggle
      }">

      <!-- conditional close button.  The BaseButton component felt like
        overkill considering the simple role this plays complicated -->
      <button
        v-if="toggle"
        class="bg-shell text-off-black absolute top-[24px] right-[32px] rounded-full h-[28px] w-[28px] text-center"
        @click.stop="toggle = false">
        <close class="h-[10px] mx-auto"/>
      </button>

      <!-- Images are passed in as slots, in this way we can use twig
        and the various transforms Craft does, when possible.
        Also, the reason why we wrap then in a divs that are conditionally
        rendered is as a kind of "hack" to disable width/height transitions
        on toggle. -->
      <div v-if="toggle">
        <slot name="image" />
      </div>

      <div v-else>
        <slot name="image" />
      </div>

      <!-- Rating -->
      <div class="flex justify-center" v-if="rating">
        <star v-for="i in parseInt(rating)" class="text-[16px] mt-[32px]" :key="i"/>
      </div>

      <!-- Content proper -->
      <div class="mt-[16px]">

        <div v-if="description && !toggle" class="leading-normal text-[15px] mb-[24px] text-center font-inter-tight">
            <p>"{{ description }}"</p>
        </div>

        <!-- This is only rendered if the user clicks on the card and
          represents a kind of long-form review. -->
        <div class="leading-normal text-[12px] mb-[24px] text-center font-inter-tight" v-if="toggle">
          <slot name="text" />
        </div>

        <p class="font-semibold text-center text-[12px] leading-tight font-inter-tight">
            {{ heading }}
        </p>

        <p class="text-center font-semibold text-[12px] leading-tight mt-[16px] font-inter">
          <span class="bg-shell rounded-full px-[21px] py-[6px] !text-off-black">
          - {{ achievement }}
          </span>
        </p>

      </div>

    </div>

    <!-- This is the "read more" text that appears on hover -->
    <div v-if="!toggle"
      class="md:h-full rounded-[24px] bg-dark-teal absolute inset-0 z-[-1] flex items-end pb-[16px] success-read-more">
      <p class="text-center text-white mx-auto text-[12px] font-inter">Read More</p>
    </div>
  </div>
</template>

<style>
.toggled img {
  width: 48px;
  height: 48px;
}
.hover-shrink > *:first-child {
  transition: height 0.4s ease;
}

@media(min-width:1025px) {
  .hover-shrink:hover > *:first-child {
    height: calc(100% - 48px);
  }
  .hover-shrink:hover > *:first-child img {
    height: calc(120px - 48px);
    width: calc(120px - 48px);
  }
}

.hover-shrink > *:first-child img {
  transition: all 0.4s ease;
}

@media (max-width:1024px) {
  .success-read-more {
    height: calc(100% + 48px);
  }
  .hover-shrink {
    margin-bottom: 48px;
  }
}
.description {
  transition: all 0.4s ease;
  height: 0;
  opacity: 0;
}
.description.toggled {
  height: auto;
  opacity: 1;
}

</style>
